import type { ReactNode } from 'react'
import { Panel } from './ui'

/**
 * The full-width 4-up audit summary strip.
 *
 * A dashboard-only fork of `StatStrip`, which stays as-is because it is nested
 * inside `ScoreHero`'s right column on the marketing surfaces. Freed from that
 * column the cells get real width, so each carries a third line: the figure
 * broken down.
 *
 * `detail` is DERIVED, never authored. The radar-v2 mock fills this line with
 * benchmark copy ("Category median 61%") that no measurement in this product
 * produces; every detail rendered here has to come from the same audit that
 * produced the value above it, or be omitted.
 */

export interface Metric {
  label: string
  value: ReactNode
  /** Trailing qualifier at small size, e.g. the "passed" in "9 passed". */
  unit?: string
  /** One line of breakdown beneath. Omit when there is nothing measured. */
  detail?: string
  /** Overrides the default foreground, e.g. pink for open issues. */
  color?: string
  /**
   * The figure is not computed yet — render `--`, not the value.
   *
   * A first-class state rather than a caller-side string swap, because the
   * failure it prevents is silent: `actions` is `[]` for the whole scan
   * (page.tsx gates generateActions on !isRunning), so a mid-run tile
   * rendered "0 open · Nothing outstanding" — a claim about a result that
   * did not exist yet. Any tile whose source is only populated at the end of
   * a run MUST set this while the run is in flight.
   */
  pending?: boolean
}

export function MetricBand({ metrics }: { metrics: Metric[] }) {
  return (
    <Panel
      as='section'
      aria-label='Audit summary'
      className='grid grid-cols-2 sm:grid-cols-4'
    >
      {metrics.map((m, i) => (
        <div
          key={m.label}
          className={[
            'flex min-h-[86px] flex-col gap-1.5 px-5 py-4 sm:px-6',
            // 2-col: rule after the first cell of each row, and under the
            // top row. 4-col: right rules only, none on the last cell.
            i % 2 === 0 ? 'border-r border-hairline' : '',
            i < metrics.length - 2 ? 'border-b border-hairline' : '',
            'sm:border-b-0',
            i === metrics.length - 1 ? 'sm:border-r-0' : 'sm:border-r',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className='instrument-label text-instrument leading-none'>
            {m.label}
          </span>
          <span
            className='font-display text-[23px] font-semibold leading-none tracking-[-0.02em]'
            style={
              m.pending
                ? { color: 'var(--pm-text-muted)' }
                : m.color
                  ? { color: m.color }
                  : undefined
            }
          >
            {m.pending ? '--' : m.value}
            {m.unit && !m.pending && (
              <span className='ml-1.5 text-xs font-medium text-muted-foreground'>
                {m.unit}
              </span>
            )}
          </span>
          {(m.pending || m.detail) && (
            <span className='block text-2xs leading-snug text-muted-foreground'>
              {m.pending ? 'Updates when the audit finishes.' : m.detail}
            </span>
          )}
        </div>
      ))}
    </Panel>
  )
}
